<template>
  <nav class="sv-nav" :class="{ 'lang-hi': lang === 'hi' }">
    <div class="nav-inner">
      <!-- Logo -->
      <div class="nav-logo" @click="$router.push('/home')" role="button" tabindex="0" @keydown.enter="$router.push('/home')">
        <span class="logo-icon">⬡</span>
        <span class="logo-text">PadhaiPlay</span>
      </div>

      <!-- Center links -->
      <div class="nav-links">
        <router-link to="/home" class="nav-link">
          <span class="nl-icon">🏠</span><span class="nl-text">{{ t('home') }}</span>
        </router-link>
        <router-link to="/academics" class="nav-link">
          <span class="nl-icon">📚</span><span class="nl-text">{{ t('learn') }}</span>
        </router-link>
        <router-link to="/arena" class="nav-link nav-link-arena">
          <span class="nl-icon">⚡</span><span class="nl-text">{{ t('arena') }}</span>
        </router-link>
        <router-link to="/wonder" class="nav-link nav-link-wonder">
          <span class="nl-icon">🧪</span><span class="nl-text">{{ lang === 'hi' ? 'जिज्ञासा' : 'Wonder' }}</span>
        </router-link>
        <router-link to="/leaderboard" class="nav-link">
          <span class="nl-icon">🏆</span><span class="nl-text">{{ t('leaderboard') }}</span>
        </router-link>
        <router-link to="/profile" class="nav-link">
          <span class="nl-icon">👤</span><span class="nl-text">{{ t('profile') }}</span>
        </router-link>
      </div>

      <!-- Right: stats + language + settings -->
      <div class="nav-right">
        <div class="stat-chip streak-chip" :class="{ dead: gameState.streak === 0 }" :title="t('dayStreak')">
          <span class="chip-icon">🔥</span>
          <span class="chip-value">{{ gameState.streak }}</span>
        </div>
        <div class="stat-chip xp-chip" :title="t('xp')">
          <span class="chip-icon">💎</span>
          <span class="chip-value">{{ gameState.xp }}</span>
          <span class="chip-level">{{ t('level') }} {{ level }}</span>
        </div>

        <!-- Quick language toggle -->
        <button class="lang-toggle" @click="toggleLanguage" :aria-label="t('language')">
          <span :class="{ active: lang === 'en' }">EN</span>
          <span class="lang-divider">/</span>
          <span :class="{ active: lang === 'hi' }">हिं</span>
        </button>

        <!-- Settings -->
        <div class="settings-wrap">
          <button class="settings-btn" @click="showSettings = !showSettings" :aria-label="t('settings')" aria-haspopup="true" :aria-expanded="showSettings ? 'true' : 'false'">⚙️</button>
          <transition name="pop" :duration="300">
            <div v-if="showSettings" class="settings-menu">
              <div class="settings-title">{{ t('settings') }}</div>

              <div class="setting-row">
                <span class="setting-label">🌐 {{ t('language') }}</span>
                <div class="seg">
                  <button :class="{ on: lang === 'en' }" @click="setLang('en')">English</button>
                  <button :class="{ on: lang === 'hi' }" @click="setLang('hi')">हिंदी</button>
                </div>
              </div>

              <div class="setting-row">
                <span class="setting-label">
                  🔍 {{ t('bigText') }}
                  <small>{{ t('bigTextDesc') }}</small>
                </span>
                <button class="switch" :class="{ on: bigText }" @click="toggleBigText" role="switch" :aria-checked="bigText ? 'true' : 'false'">
                  <span class="knob"></span>
                </button>
              </div>

              <div class="setting-row">
                <span class="setting-label">🔊 {{ t('soundEffects') }}</span>
                <button class="switch" :class="{ on: sfxOn }" @click="toggleSfx" role="switch" :aria-checked="sfxOn ? 'true' : 'false'">
                  <span class="knob"></span>
                </button>
              </div>

              <div class="setting-row">
                <span class="setting-label">
                  🎓 {{ lang === 'hi' ? 'मेरी कक्षा' : 'My Class' }}
                  <small>{{ lang === 'hi' ? 'सवाल आपकी कक्षा के हिसाब से' : 'Questions match your class level' }}</small>
                </span>
                <select class="grade-pick" :value="myGrade" @change="setGrade($event.target.value)">
                  <option v-for="g in [5, 6, 7, 8, 9, 10, 11, 12]" :key="g" :value="g">{{ g <= 10 ? t('grade' + g) : (lang === 'hi' ? 'कक्षा ' + g : 'Class ' + g) }}</option>
                </select>
              </div>

              <!-- Interests: the same list onboarding asks for, editable here
                   so Home can be widened or narrowed at any time. These are
                   SIBLINGS of the class row — nested inside it they inherited
                   its `display:flex` and laid out sideways off the panel. -->
              <div class="settings-title interests-title">{{ lang === 'hi' ? 'मेरी रुचियाँ' : 'My interests' }}</div>
              <div class="interest-chips">
                <button
                  v-for="it in INTERESTS"
                  :key="it.id"
                  class="interest-chip"
                  :class="{ on: myInterests.includes(it.id) }"
                  @click="toggleInterest(it.id)"
                >{{ it.icon }} {{ it.name[lang] }}</button>
              </div>
              <div class="interest-hint">
                {{ myInterests.length
                  ? (lang === 'hi' ? 'होम पर यही दिखेंगे' : 'Home leads with these')
                  : (lang === 'hi' ? 'कुछ नहीं चुना — सब दिख रहा है' : 'Nothing picked — showing everything') }}
              </div>

              <button v-if="!guest" class="logout-row" @click="logout">🚪 {{ t('logout') }}</button>
              <!-- A guest has nothing to log out of; offer the upgrade instead. -->
              <button v-else class="signin-row" @click="$router.push({ path: '/login', query: { redirect: $route.fullPath } })">
                💾 {{ lang === 'hi' ? 'खाता बनाओ / लॉग इन' : 'Sign up / Log in' }}
              </button>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
import { languageState, t, setLanguage } from '@/services/translationService';
import { gameState, levelForXP, refreshStreak, pullFromServer, badgeQueue, INTERESTS, setInterests, prefs, isGuest } from '@/services/game';
import { sfxEnabled, setSfxEnabled, playClick } from '@/services/sfx';

export default {
  name: 'NavBar',
  data() {
    return {
      languageState,
      gameState,
      showSettings: false,
      bigText: localStorage.getItem('bigText') === '1',
      sfxOn: sfxEnabled(),
      myGrade: Number(localStorage.getItem('myGrade')) || 7,
      INTERESTS,
      prefs
    };
  },
  computed: {
    guest() { return isGuest(); },
    myInterests() { return this.prefs.interests; },
    lang() { return this.languageState.currentLanguage; },
    level() { return levelForXP(this.gameState.xp); }
  },
  created() {
    if (refreshStreak() === 'saved') {
      badgeQueue.push({
        icon: '🛡️',
        name: { en: 'Streak Saved!', hi: 'स्ट्रीक बच गई!' },
        desc: { en: 'Your shield protected your streak. Play today to keep it going!', hi: 'तुम्हारे कवच ने स्ट्रीक बचा ली। आज खेलकर इसे जारी रखो!' }
      });
    }
    pullFromServer();
    if (this.bigText) document.documentElement.classList.add('big-text');
  },
  mounted() {
    document.addEventListener('click', this.onOutsideClick);
    // Re-check the streak when the tab comes back into focus, so an
    // always-open tab doesn't show a stale streak across midnight
    document.addEventListener('visibilitychange', this.onVisible);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick);
    document.removeEventListener('visibilitychange', this.onVisible);
  },
  methods: {
    t(key) { return t(key); },
    toggleLanguage() {
      setLanguage(this.lang === 'en' ? 'hi' : 'en');
    },
    setLang(l) { setLanguage(l); },
    toggleBigText() {
      this.bigText = !this.bigText;
      localStorage.setItem('bigText', this.bigText ? '1' : '0');
      document.documentElement.classList.toggle('big-text', this.bigText);
    },
    toggleSfx() {
      this.sfxOn = !this.sfxOn;
      setSfxEnabled(this.sfxOn);
      if (this.sfxOn) playClick();
    },
    toggleInterest(id) {
      const next = this.myInterests.includes(id)
        ? this.myInterests.filter(x => x !== id)
        : [...this.myInterests, id];
      this.myInterests = setInterests(next);
    },
    setGrade(g) {
      this.myGrade = Number(g);
      localStorage.setItem('myGrade', String(this.myGrade));
    },
    onVisible() {
      if (!document.hidden) refreshStreak();
    },
    onOutsideClick(e) {
      if (this.showSettings && !this.$el.querySelector('.settings-wrap').contains(e.target)) {
        this.showSettings = false;
      }
    },
    logout() {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUsername');
      this.$router.push('/login');
    }
  }
};
</script>

<style scoped>
.sv-nav {
  position: fixed;
  top: 0; left: 0; right: 0;
  z-index: 1000;
  background: #FFFFFF;
  border-bottom: 3px solid #E8E0D2;
}
.nav-inner {
  max-width: 1360px;
  margin: 0 auto;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: 0 22px;
  min-width: 0; /* let the links column shrink instead of shoving .nav-right off-screen */
}
.nav-logo {
  display: flex; align-items: center; gap: 9px;
  cursor: pointer; user-select: none;
  flex: 0 0 auto;
}
.nav-logo:hover .logo-icon { animation: sv-wiggle 0.5s ease; }
.logo-icon { font-size: 1.6rem; }
.logo-text {
  font-weight: 800; font-size: 1.15rem; color: #58CC02; letter-spacing: -0.3px;
}

/* The 6 nav links are the only flexible column. Letting them shrink and
   scroll means the streak/XP chips and settings can never be pushed past the
   right edge — that used to happen on ordinary 1024px and 1280px laptops. */
.nav-links {
  display: flex; gap: 6px;
  flex: 0 1 auto; min-width: 0;
  overflow-x: auto;
  scrollbar-width: none; -ms-overflow-style: none;
}
.nav-links::-webkit-scrollbar { display: none; }
.nav-right { flex: 0 0 auto; }
.nav-link {
  display: flex; align-items: center; gap: 7px;
  padding: 8px 16px; border-radius: 14px;
  color: #8E8577;
  text-decoration: none;
  font-weight: 700; font-size: 0.95rem;
  transition: all 0.15s;
  min-height: 44px;
  border: 2px solid transparent;
}
.nav-link:hover { background: #FFF4CC; color: #4B4B4B; }
.nav-link.router-link-active {
  color: #1CB0F6; background: #DDF4FF; border-color: #A8E1FF;
}
.nav-link-arena.router-link-active { color: #E08600; background: #FFEFD8; border-color: #FFD9A0; }
.nav-link-wonder.router-link-active { color: #A560E8; background: #F5E9FF; border-color: #E3C8FF; }
.nl-icon { font-size: 1.15rem; }

.nav-right { display: flex; align-items: center; gap: 9px; }

.stat-chip {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 14px;
  border-radius: 30px;
  font-weight: 800; font-size: 0.95rem; color: #4B4B4B;
  border: 2px solid;
}
.streak-chip { background: #FFEFD8; border-color: #FFD9A0; color: #E08600; }
.streak-chip.dead { opacity: 0.55; filter: grayscale(0.8); }
.xp-chip { background: #DDF4FF; border-color: #A8E1FF; color: #1899D6; }
.chip-level {
  font-size: 0.72rem; font-weight: 800;
  color: #8E8577;
  border-left: 2px solid #A8E1FF;
  padding-left: 7px;
}

.lang-toggle {
  display: flex; align-items: center; gap: 4px;
  background: #FFFFFF;
  border: 2px solid #E8E0D2;
  border-radius: 30px;
  padding: 7px 14px;
  cursor: pointer;
  font-weight: 800; font-size: 0.88rem;
  color: #C9BFA9;
  min-height: 42px;
  box-shadow: 0 3px 0 #E8E0D2;
  transition: all 0.1s;
}
.lang-toggle:hover { border-color: #A8E1FF; }
.lang-toggle:active { transform: translateY(2px); box-shadow: 0 1px 0 #E8E0D2; }
.lang-toggle .active { color: #1CB0F6; }
.lang-divider { color: #E8E0D2; }

.settings-wrap { position: relative; }
.settings-btn {
  width: 44px; height: 44px;
  display: flex; align-items: center; justify-content: center;
  background: #FFFFFF;
  border: 2px solid #E8E0D2;
  border-radius: 50%;
  font-size: 1.2rem;
  cursor: pointer;
  box-shadow: 0 3px 0 #E8E0D2;
  transition: all 0.15s;
}
.settings-btn:hover { transform: rotate(30deg); border-color: #FFD9A0; }

.settings-menu {
  position: absolute; top: 54px; right: 0;
  width: 300px;
  background: #FFFFFF;
  border: 2px solid #E8E0D2;
  border-radius: 20px;
  padding: 18px;
  box-shadow: 0 6px 0 #E8E0D2, 0 18px 40px rgba(90, 70, 30, 0.12);
}
.settings-title {
  font-weight: 800; font-size: 1.05rem; color: #3C3C3C;
  margin-bottom: 14px;
}
.setting-row {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px; padding: 12px 0;
  border-top: 2px solid #F5EFE3;
}
.setting-label {
  font-size: 0.95rem; font-weight: 700; color: #4B4B4B;
  display: flex; flex-direction: column;
}
.setting-label small { color: #8E8577; font-weight: 500; font-size: 0.76rem; }

.seg { display: flex; border-radius: 12px; overflow: hidden; border: 2px solid #E8E0D2; }
.seg button {
  padding: 8px 14px; min-height: 42px;
  background: #FFFFFF; border: none;
  color: #8E8577;
  font-weight: 800; font-size: 0.86rem;
  cursor: pointer;
  font-family: inherit;
}
.seg button.on { background: #DDF4FF; color: #1899D6; }

.grade-pick {
  padding: 8px 12px; min-height: 42px;
  border: 2px solid #E8E0D2; border-radius: 12px;
  background: #FFFFFF; color: #4B4B4B;
  font-family: inherit; font-weight: 800; font-size: 0.88rem;
  cursor: pointer;
}
.grade-pick:focus { border-color: #A8E1FF; outline: none; }

.switch {
  width: 54px; height: 32px;
  border-radius: 30px;
  background: #E8E0D2;
  border: none;
  position: relative;
  cursor: pointer;
  transition: background 0.25s;
  flex-shrink: 0;
}
.switch .knob {
  position: absolute; top: 4px; left: 5px;
  width: 24px; height: 24px;
  border-radius: 50%;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
  transition: transform 0.25s;
}
.switch.on { background: #58CC02; }
.switch.on .knob { transform: translateX(21px); }

.logout-row {
  width: 100%;
  margin-top: 12px;
  padding: 11px;
  border-radius: 14px;
  border: 2px solid #FFC1C1;
  background: #FFE3E3;
  color: #EA2B2B;
  font-weight: 800; font-size: 0.92rem;
  cursor: pointer;
  box-shadow: 0 3px 0 #FFC1C1;
  transition: all 0.1s;
  font-family: inherit;
}
.logout-row:active { transform: translateY(2px); box-shadow: 0 1px 0 #FFC1C1; }

.pop-enter-active, .pop-leave-active { transition: all 0.18s ease; }
.pop-enter-from, .pop-leave-to { opacity: 0; transform: translateY(-8px) scale(0.97); }

/* Laptops (~1024-1300px) can't fit six labelled links plus the chips, so trim
   the padding there before falling back to icon-only below 900px. */
@media (max-width: 1300px) {
  .nav-link { padding: 8px 11px; font-size: 0.9rem; gap: 5px; }
  .nav-inner { gap: 10px; padding: 0 16px; }
}

@media (max-width: 900px) {
  .nl-text { display: none; }
  .nav-link { padding: 9px 11px; }
  .logo-text { display: none; }
  .chip-level { display: none; }
  .user-none { display: none; }
}
@media (max-width: 560px) {
  .nav-inner { padding: 0 10px; gap: 6px; }
  .stat-chip { padding: 6px 9px; font-size: 0.8rem; }
}

/* ── TABLET / PHONE: tap targets ───────────────────────────────────────── */
@media screen and (max-width: 768px) {
  /* 42px controls are just under the 44px minimum comfortable touch size */
  .lang-toggle,
  .seg button,
  .grade-pick { min-height: 44px; }
}

/* ── PHONE (≤480px) ────────────────────────────────────────────────────────
   The bar holds 6 links + 2 stat chips + language toggle + settings, which
   measures ~430px of content on a 375px screen. The bar height must stay at
   64px (App.vue offsets the page by a fixed 62px), so instead of wrapping we
   shrink the fixed clusters and turn the link row into a swipeable strip. */
@media screen and (max-width: 480px) {
  .nav-inner { padding: 0 8px; gap: 6px; min-width: 0; }

  .nav-logo { flex: 0 0 auto; }
  .logo-icon { font-size: 1.4rem; }

  /* Links scroll horizontally instead of overflowing the viewport */
  .nav-links {
    flex: 1 1 auto;
    min-width: 0;
    gap: 2px;
    overflow-x: auto;
    overflow-y: hidden;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    scroll-snap-type: x proximity;
  }
  .nav-links::-webkit-scrollbar { display: none; }
  .nav-link {
    flex: 0 0 auto;
    padding: 9px 8px;
    scroll-snap-align: center;
  }

  .nav-right { flex: 0 0 auto; gap: 5px; }
  .stat-chip { padding: 5px 8px; gap: 4px; font-size: 0.8rem; }
  .chip-icon { font-size: 0.95rem; }

  /* Keep the toggle (it still switches languages on tap) but show only the
     language you are currently on, so it costs ~46px instead of ~75px */
  .lang-toggle {
    padding: 0 8px;
    min-width: 46px;
    justify-content: center;
    font-size: 0.82rem;
  }
  .lang-toggle > span:not(.active) { display: none; }

  .settings-btn { flex: 0 0 auto; }

  /* Dropdown must never be wider than the phone */
  .settings-menu {
    width: 290px;
    max-width: calc(100vw - 20px);
    padding: 16px 14px;
    border-radius: 18px;
  }
  .setting-row { gap: 8px; }
  .setting-label { font-size: 0.9rem; min-width: 0; overflow-wrap: break-word; }
  .setting-label small { font-size: 0.72rem; }
  .seg button { padding: 8px 10px; font-size: 0.8rem; }
  .grade-pick { padding: 8px 10px; }
  /* The 32px-tall switch is under the 44px tap minimum — grow the hit area
     without changing how it looks */
  .switch::after { content: ''; position: absolute; inset: -7px; }
}

.interests-title { margin-top: 10px; }
.interest-chips { display: flex; flex-wrap: wrap; gap: 6px; padding: 4px 0 2px; }
.interest-chip {
  padding: 7px 10px; min-height: 36px; border-radius: 18px;
  border: 2px solid #E8E0D2; background: #FFFFFF; color: #8E8577;
  font-family: inherit; font-weight: 800; font-size: 0.72rem; cursor: pointer;
}
.interest-chip.on { background: #E7F9D9; border-color: #C4EC9E; color: #46A302; }
.interest-hint { font-size: 0.68rem; font-weight: 700; color: #A79C88; padding: 4px 0 2px; }

.signin-row {
  width: 100%; margin-top: 10px; padding: 11px; min-height: 44px;
  border-radius: 14px; background: #DDF4FF; border: 2px solid #A8E1FF;
  color: #1899D6; font-family: inherit; font-weight: 800; font-size: .86rem;
  cursor: pointer; box-shadow: 0 3px 0 #A8E1FF;
}
.signin-row:active { transform: translateY(2px); box-shadow: 0 1px 0 #A8E1FF; }
</style>
